/* eslint-disable no-nested-ternary */
import React, { useEffect } from 'react';
import {
  Container,
  // TextField,
  Button,
  MenuItem,
  Box,
  Typography,
  Alert,
  AlertTitle,
  Grid,
} from '@mui/material';
import {
  useCreateNotificationMutation,
  useUpdateNotificationMutation,
} from 'renderer/features/notifications/services/notifications.endpoints';
import { Field, Form, Formik, useFormikContext } from 'formik';
import { toValidator } from 'renderer/shared/schemas/common.schemas';
import { CheckboxWithLabel, TextField } from 'formik-mui';
import { DateTimePicker } from 'formik-mui-lab';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { dropdownKeyEventHandler } from 'renderer/shared/callbacks/keyEventHandler';
import {
  NotificationFormSchema,
  NotificationForm as NotificationFormType,
  NotificationType,
} from '../schemas/notifications.schemas';

interface ModeListenerProps {
  mode: 'edit' | 'create';
  notification: NotificationType | null;
}

const ModeListener: React.FC<ModeListenerProps> = ({ mode, notification }) => {
  const { setValues } = useFormikContext<NotificationFormType>();

  useEffect(() => {
    if (mode === 'edit' && notification) {
      setValues(notification);
    }
  }, [mode, notification, setValues]);
  return null;
};

interface NotificationFormProps {
  mode: 'edit' | 'create';
  notification: NotificationType | null;
}

const initialValues: NotificationFormType = {
  title: '',
  message: '',
  severity: 'info',
  showIcon: true,
  dateTime: '',
};

const NotificationForm: React.FC<NotificationFormProps> = ({
  mode,
  notification,
}) => {
  const [createNotification, { isLoading: createLoading, error: createError }] =
    useCreateNotificationMutation();
  const [editNotification, { isLoading: editLoading, error: editError }] =
    useUpdateNotificationMutation();

  const isLoading = createLoading || editLoading;

  const handleSubmit = (values: NotificationFormType) => {
    // e.preventDefault();
    if (mode === 'edit' && notification) {
      editNotification({ notificationId: notification._id, ...values });
      return;
    }
    createNotification(values);
  };

  const parseContent = (content: string) => {
    const parts = content.split(/(<b>.*?<\/b>)/g);
    return parts.map((part) => {
      if (part.startsWith('<b>') && part.endsWith('</b>')) {
        return <b>{part.replace(/<\/?b>/g, '')}</b>;
      }
      return part;
    });
  };

  const themeOptions = [
    { label: 'Info', value: 'info' },
    { label: 'Warning', value: 'warning' },
    { label: 'Error', value: 'error' },
    { label: 'Success', value: 'success' },
  ];

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={toValidator(NotificationFormSchema)}
    >
      {({ values, setFieldValue }) => (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Container>
              <Form>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {editError && (
                    <Alert severity="error">
                      {formatQueryError(editError)}
                    </Alert>
                  )}
                  {createError && (
                    <Alert severity="error">
                      {formatQueryError(createError)}
                    </Alert>
                  )}
                  <ModeListener mode={mode} notification={notification} />
                  <Field
                    component={TextField}
                    name="title"
                    label="Title"
                    disabled={isLoading}
                    required
                  />
                  <Field
                    component={TextField}
                    name="message"
                    label="Message"
                    disabled={isLoading}
                    required
                    multiline
                    rows={4}
                    // helperText="Use <b></b> tags to make text bold"
                  />
                  <Field
                    component={TextField}
                    select
                    onKeyUp={dropdownKeyEventHandler(themeOptions, (option) =>
                      setFieldValue('severity', option),
                    )}
                    name="severity"
                    label="Theme"
                    disabled={isLoading}
                    required
                  >
                    {themeOptions.map(({ label, value }) => (
                      <MenuItem value={value}>{label}</MenuItem>
                    ))}
                    {/* <MenuItem value="info">Info</MenuItem>
                    <MenuItem value="warning">Warning</MenuItem>
                    <MenuItem value="error">Error</MenuItem>
                    <MenuItem value="success">Success</MenuItem> */}
                  </Field>
                  <Field
                    component={CheckboxWithLabel}
                    type="checkbox"
                    name="showIcon"
                    Label={{ label: 'Show Icon' }}
                    disabled={isLoading}
                  />
                  <Field
                    component={DateTimePicker}
                    name="dateTime"
                    label="Notification Date and Time"
                  />
                  <Button type="submit" variant="contained" color="primary">
                    {mode === 'edit'
                      ? 'Update Notification'
                      : 'Create Notification'}
                  </Button>
                </Box>
              </Form>
            </Container>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Preview:</Typography>
            <Alert
              severity={values.severity}
              {...(!values.showIcon && { icon: false })}
              sx={{
                mb: 2,
                border: `1px solid ${
                  values.severity === 'info'
                    ? '#1976d2'
                    : values.severity === 'warning'
                    ? '#ff9800'
                    : values.severity === 'error'
                    ? '#d32f2f'
                    : '#2e7d32'
                }`,
                borderRadius: '5px',
              }}
            >
              <AlertTitle>{values.title}</AlertTitle>
              {parseContent(values.message)}
            </Alert>
          </Grid>
        </Grid>
      )}
    </Formik>
  );
};

export default NotificationForm;
